/**
 * useAnalytics — lightweight page_view + app_open tracker for authenticated
 * users only. Guests are never tracked (backend enforces this too).
 *
 * Design (iter105, 2026-02):
 * - Fires `app_open` ONCE per session on mount (when user is authenticated).
 * - Fires `page_view` on every route change (debounced against duplicates).
 * - Non-blocking: errors are swallowed so analytics can never break UX.
 * - Platform detection reads Capacitor.getPlatform() → 'android' / 'ios' / 'web'.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Capacitor } from '@capacitor/core';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function detectPlatform() {
  try {
    const p = (Capacitor.getPlatform && Capacitor.getPlatform()) || 'web';
    if (p === 'android' || p === 'ios') return p;
    return 'web';
  } catch {
    return 'web';
  }
}

async function sendEvent(event_type, path) {
  try {
    await axios.post(
      `${API}/analytics/track`,
      { event_type, path: path || '', platform: detectPlatform() },
      { timeout: 5000 }
    );
  } catch {
    // Analytics is best-effort — never surface errors.
  }
}

/**
 * Hook this once inside AppRouter (or any component that lives under
 * BrowserRouter). Pass the currently authenticated user; when null, nothing
 * is tracked.
 */
export function useAnalytics(user) {
  const location = useLocation();
  const openFiredRef = useRef(false);
  const lastPathRef = useRef(null);

  // app_open: fire once per session, once the user is authenticated.
  useEffect(() => {
    if (!user || openFiredRef.current) return;
    openFiredRef.current = true;
    sendEvent('app_open', location.pathname);
  }, [user, location.pathname]);

  // page_view: fire on every real path change (skip repeats).
  useEffect(() => {
    if (!user) return;
    const path = location.pathname;
    if (lastPathRef.current === path) return;
    lastPathRef.current = path;
    // Skip the very first render if it already coincides with app_open
    // (both would double-count the landing page).
    if (openFiredRef.current && lastPathRef.current === location.pathname) {
      // First render after app_open — still send page_view so the KPI
      // "views" reflects total pages consumed, not just app opens.
      sendEvent('page_view', path);
    }
  }, [user, location.pathname]);
}
